import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, CalendarClock, ShieldCheck, Package, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";

const fmtTime = (s) => {
    const d = new Date(s);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const TYPE_ICON = {
    subscription_refill_due: <CalendarClock className="h-4 w-4" />,
    prescription_verified: <ShieldCheck className="h-4 w-4" />,
    order_shipped: <Package className="h-4 w-4" />,
    promo: <Sparkles className="h-4 w-4" />,
};

const linkForNotif = (n) => {
    if (n.type === "subscription_refill_due" && n.related_id) return `/subscriptions/${n.related_id}`;
    if (n.type === "order_shipped" && n.related_id) return `/orders/${n.related_id}`;
    if (n.type === "prescription_verified" && n.related_id) return `/prescriptions/${n.related_id}`;
    return null;
};

const Row = ({ n, onMarkRead }) => {
    const href = linkForNotif(n);
    const Wrapper = ({ children }) =>
        href ? (
            <Link to={href} className="block" onClick={() => !n.is_read && onMarkRead(n.id)}>{children}</Link>
        ) : (
            <div>{children}</div>
        );

    return (
        <li
            className={`group rounded-2xl border p-4 transition-all ${
                n.is_read ? "border-zinc-100 bg-white" : "border-brand-100 bg-brand-50/40"
            }`}
            data-testid={`notif-row-${n.id}`}
        >
            <Wrapper>
                <div className="flex items-start gap-3">
                    <span
                        className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1 ${
                            n.is_read ? "bg-zinc-100 text-zinc-500 ring-zinc-200" : "bg-brand-100 text-brand-700 ring-brand-200"
                        }`}
                    >
                        {TYPE_ICON[n.type] || <Bell className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                            <p className={`text-sm ${n.is_read ? "text-zinc-700" : "font-semibold text-zinc-900"}`}>{n.title}</p>
                            {!n.is_read && <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-brand-600" data-testid={`notif-dot-${n.id}`} />}
                        </div>
                        <p className={`mt-1 text-sm ${n.is_read ? "text-zinc-500" : "text-zinc-700"}`}>{n.body}</p>
                        <p className="mt-1.5 text-[11px] uppercase tracking-wide text-zinc-400">{fmtTime(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                        <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMarkRead(n.id); }}
                            className="rounded-full px-3 py-1 text-[11px] font-medium text-brand-700 hover:bg-brand-100"
                            data-testid={`notif-mark-read-${n.id}`}
                        >
                            Mark read
                        </button>
                    )}
                </div>
            </Wrapper>
        </li>
    );
};

const NotificationsContent = () => {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/notifications", {
                params: { page: 1, page_size: 50, unread_only: filter === "unread" },
            });
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filter]); // eslint-disable-line

    const markRead = async (id) => {
        // optimistic
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
        setUnread((u) => Math.max(0, u - 1));
        try {
            await api.post(`/notifications/${id}/read`);
        } catch {
            toast.error("Could not mark read");
            await load();
        }
    };

    const markAll = async () => {
        try {
            await api.post("/notifications/read-all");
            toast.success("All caught up");
            await load();
        } catch {
            toast.error("Could not mark all read");
        }
    };

    return (
        <div data-testid="notifications-page">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {["all", "unread"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`rounded-full px-4 py-1.5 text-xs font-medium capitalize transition-colors ${
                                filter === f
                                    ? "bg-brand-700 text-white shadow-sm"
                                    : "border border-zinc-200 text-zinc-700 hover:border-brand-300 hover:text-brand-700"
                            }`}
                            data-testid={`notif-filter-${f}`}
                        >
                            {f}{f === "unread" && unread > 0 ? ` (${unread})` : ""}
                        </button>
                    ))}
                </div>
                {unread > 0 ? (
                    <button
                        onClick={markAll}
                        className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-4 py-1.5 text-xs font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700"
                        data-testid="notif-mark-all-btn"
                    >
                        <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                    </button>
                ) : (
                    <button
                        disabled
                        className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-full border border-zinc-100 px-4 py-1.5 text-xs font-medium text-zinc-400"
                        data-testid="notif-mark-all-btn"
                        aria-disabled="true"
                    >
                        <CheckCheck className="h-3.5 w-3.5" /> All caught up
                    </button>
                )}
            </div>

            {loading ? (
                <ul className="space-y-3">
                    {[0,1,2,3].map((i) => <li key={i} className="h-20 skeleton rounded-2xl" />)}
                </ul>
            ) : items.length === 0 ? (
                <EmptyState
                    icon={<Bell className="h-7 w-7" />}
                    title={filter === "unread" ? "You're all caught up" : "No notifications yet"}
                    description="We'll let you know when it's time to refill, when prescriptions are verified, or when your orders move."
                />
            ) : (
                <ul className="space-y-3" data-testid="notifications-list">
                    {items.map((n) => <Row key={n.id} n={n} onMarkRead={markRead} />)}
                </ul>
            )}
        </div>
    );
};

const Notifications = () => (
    <Layout>
        <section className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
            <header className="mb-8">
                <p className="label-uppercase">Inbox</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Notifications</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-500">
                    Refill reminders, prescription updates, order shipments and offers — all in one place.
                </p>
            </header>
            <AuthGuard>
                <NotificationsContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Notifications;
